/**
 * Lets the Contour+ model-selection form act on a model the clinician
 * accepted in the agent chat. It drives the form ONLY through the form's own
 * handlers (select model → select customization → the Start button's
 * `onSubmit`), one step per render, then clears the request. With no request
 * (the manual flow) it does nothing.
 */
import { useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { agentContourModelRequestState } from '~/state/atoms/chatAtoms';
import type { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';

export interface ContourFormBinding {
    readonly availableModels: ReadonlyArray<PredictionModel>;
    /** False while models/customizations load or the form is disabled. */
    readonly ready: boolean;
    readonly isSubmitting: boolean;
    readonly selectedModel?: PredictionModel;
    readonly selectedCustomization?: string;
    readonly selectedStructures: ReadonlyArray<string>;
    selectModel(model: PredictionModel): void;
    selectCustomization(customizationName?: string): void;
    /** Reset search/body-part filters so the selected model is visible. */
    clearFilters(): void;
    submit(
        model: PredictionModel,
        selectedStructures: string[],
        selectedCustomization?: string
    ): void;
}

export function useAgentContourModelRequest(form: ContourFormBinding): void {
    const [request, setRequest] = useRecoilState(agentContourModelRequestState);
    const {
        availableModels,
        ready,
        isSubmitting,
        selectedModel,
        selectedCustomization,
        selectedStructures,
        selectModel,
        selectCustomization,
        clearFilters,
        submit,
    } = form;

    useEffect(() => {
        if (!request || !ready || isSubmitting) {
            return;
        }
        const model = availableModels.find((m) => m.name === request.modelName);
        if (!model) {
            setRequest(null);
            return;
        }
        if (selectedModel?.name !== model.name) {
            clearFilters();
            selectModel(model);
            return;
        }
        if (selectedCustomization !== request.customizationName) {
            selectCustomization(request.customizationName);
            return;
        }
        // Same guard as the Start button: never submit without structures.
        if (request.autoStart && selectedStructures.length > 0) {
            submit(model, [...selectedStructures], selectedCustomization);
        }
        setRequest(null);
    }, [
        request,
        ready,
        isSubmitting,
        availableModels,
        selectedModel,
        selectedCustomization,
        selectedStructures,
        selectModel,
        selectCustomization,
        clearFilters,
        submit,
        setRequest,
    ]);
}
